import { Settings } from 'lucide-react';
import Link from 'next/link';
import { useState } from 'react';
import { paths } from 'src/config/paths';
import { useProjectsContext } from 'src/modules/projects/context';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Switch,
  Tooltip,
} from '@/design-system/components/ui';

import { useAffiliateApplicationFormConfig } from '../hooks/useAffiliateApplicationFormConfig';
import { useEnableFormConfig } from '../hooks/useEnableFormConfig';

interface FormConfigHeaderActionsProps {
  slug: string;
}

/**
 * Renders the Affiliate Applications page header actions: form-enabled badge,
 * enable/disable switch, and the Configure Form link. Self-contained — owns
 * its own data fetching and the disable-confirmation dialog. Returns `null`
 * while the form config is loading or not configured (the page-body empty
 * state already exposes a "Create Form" CTA in that case).
 */
export const FormConfigHeaderActions = ({ slug }: FormConfigHeaderActionsProps) => {
  const { project } = useProjectsContext();
  const { formConfig, isLoading, isNotFound } = useAffiliateApplicationFormConfig({
    projectId: project?.id || '',
  });
  const { enableFormConfig, isLoading: isEnabling } = useEnableFormConfig();
  const [disableDialogOpen, setDisableDialogOpen] = useState(false);

  if (isLoading || isNotFound || !formConfig) return null;

  const handleToggleEnabled = (checked: boolean) => {
    if (!project?.id) return;
    if (!checked) {
      setDisableDialogOpen(true);
      return;
    }
    enableFormConfig({ projectId: project.id, enabled: true });
  };

  const handleConfirmDisable = () => {
    if (!project?.id) return;
    enableFormConfig({ projectId: project.id, enabled: false });
    setDisableDialogOpen(false);
  };

  return (
    <>
      <Tooltip content="When disabled, the application form will not be available and all new applications will be rejected.">
        <div className="tw:flex tw:items-center tw:gap-2">
          <Badge variant={formConfig.enabled ? 'success' : 'error'}>
            <span
              className={
                formConfig.enabled
                  ? 'tw:w-2 tw:h-2 tw:rounded-full tw:bg-green-500'
                  : 'tw:w-2 tw:h-2 tw:rounded-full tw:bg-red-500'
              }
            />
            {formConfig.enabled ? 'Form Enabled' : 'Form Disabled'}
          </Badge>
          <Switch checked={formConfig.enabled} onCheckedChange={handleToggleEnabled} disabled={isEnabling} />
        </div>
      </Tooltip>
      <Button asChild variant="outline" size="sm">
        <Link href={paths.project.affiliateManagement.applicationFormBuilder(slug)}>
          <Settings size={16} aria-hidden="true" />
          Configure Form
        </Link>
      </Button>

      <AlertDialog open={disableDialogOpen} onOpenChange={setDisableDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disable Application Form</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to disable the application form? The form will no longer be available and all new
              applications will be rejected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button variant="outline">Cancel</Button>
            </AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button variant="destructive" onClick={handleConfirmDisable}>
                Disable Form
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
